import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Brain, Search, MessageSquare, Database } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Memory {
  id: string;
  memory: string;
  category: string;
  relevanceScore: number;
  createdAt: string;
  updatedAt: string;
  metadata?: any;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export default function Mem0TestPage() {
  const [userId, setUserId] = useState('test_user');
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const { toast } = useToast();

  // Chat with memory mutation
  const chatMutation = useMutation({
    mutationFn: async (data: { message: string; userId: string }) => {
      const response = await fetch('/api/ai/memory/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Chat failed');
      return response.json();
    },
    onSuccess: (data) => {
      setChatHistory((prev) => [
        ...prev,
        { role: 'user', content: message, timestamp: new Date() },
        { role: 'assistant', content: data.response, timestamp: new Date() },
      ]);
      setMessage('');
      toast({
        title: 'Response received',
        description: `Extracted ${data.memoriesExtracted || 0} new memories`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Chat failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  // Search memories mutation
  const searchMutation = useMutation({
    mutationFn: async (data: { query: string; userId: string; limit?: number }) => {
      const response = await fetch('/api/ai/memory/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Search failed');
      return response.json();
    },
  });

  // Get user memories query
  const { data: userMemories, refetch: refetchMemories } = useQuery({
    queryKey: ['memories', userId],
    queryFn: async () => {
      const response = await fetch(`/api/ai/memory/user/${userId}`);
      if (!response.ok) throw new Error('Failed to fetch memories');
      return response.json();
    },
    enabled: !!userId,
  });

  // Memory health check query
  const { data: healthCheck } = useQuery({
    queryKey: ['memory-health'],
    queryFn: async () => {
      const response = await fetch('/api/ai/memory/health');
      if (!response.ok) throw new Error('Health check failed');
      return response.json();
    },
    refetchInterval: 30000, // Check every 30 seconds
  });

  const handleChat = () => {
    if (!message.trim()) return;
    chatMutation.mutate({ message, userId });
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    searchMutation.mutate({ query: searchQuery, userId, limit: 5 });
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      personal: 'bg-blue-100 text-blue-800',
      preference: 'bg-green-100 text-green-800',
      context: 'bg-yellow-100 text-yellow-800',
      relationship: 'bg-purple-100 text-purple-800',
      technical: 'bg-red-100 text-red-800',
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Mem0 Persistent Memory Test</h1>
        <p className="text-gray-600">Test AI memory integration with Gemini</p>

        {/* Health Status */}
        <div className="flex justify-center items-center gap-2 mt-4">
          <Database className="h-4 w-4" />
          <span className={`text-sm ${healthCheck?.success ? 'text-green-600' : 'text-red-600'}`}>
            Memory System: {healthCheck?.success ? 'Healthy' : 'Unavailable'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chat Interface */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Chat with Memory
            </CardTitle>
            <CardDescription>Have a conversation with AI that remembers context</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="User ID (e.g., customer_123)"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-40"
              />
            </div>

            {/* Chat History */}
            <ScrollArea className="h-64 w-full border rounded-md p-4">
              {chatHistory.length === 0 ? (
                <p className="text-gray-500 text-center">Start a conversation...</p>
              ) : (
                <div className="space-y-3">
                  {chatHistory.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {msg.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Message Input */}
            <div className="flex gap-2">
              <Textarea
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleChat();
                  }
                }}
                className="flex-1"
                rows={2}
              />
              <Button
                onClick={handleChat}
                disabled={chatMutation.isPending || !message.trim()}
                className="self-end"
              >
                {chatMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Memory Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Memories
            </CardTitle>
            <CardDescription>Search through stored memories for the user</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search memories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className="flex-1"
              />
              <Button
                onClick={handleSearch}
                disabled={searchMutation.isPending || !searchQuery.trim()}
              >
                {searchMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
              </Button>
            </div>

            {/* Search Results */}
            <ScrollArea className="h-64 w-full">
              {searchMutation.data?.results?.length > 0 ? (
                <div className="space-y-3">
                  {searchMutation.data.results.map((memory: any) => (
                    <Card key={memory.id} className="p-3">
                      <div className="flex justify-between items-start mb-2">
                        <Badge className={getCategoryColor(memory.category)}>
                          {memory.category}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          Score: {memory.relevanceScore.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-sm">{memory.memory}</p>
                    </Card>
                  ))}
                </div>
              ) : searchMutation.data ? (
                <p className="text-gray-500 text-center">No memories found</p>
              ) : (
                <p className="text-gray-500 text-center">Enter a search query</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* All User Memories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            All Memories for User: {userId}
          </CardTitle>
          <CardDescription>
            Complete memory storage for the current user ({userMemories?.count || 0} memories)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-96 w-full">
            {userMemories?.memories?.length > 0 ? (
              <div className="grid gap-3">
                {userMemories.memories.map((memory: Memory) => (
                  <Card key={memory.id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <Badge className={getCategoryColor(memory.category)}>{memory.category}</Badge>
                      <div className="text-xs text-gray-500 text-right">
                        <div>Score: {memory.relevanceScore.toFixed(2)}</div>
                        <div>{new Date(memory.updatedAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <p className="text-sm mb-2">{memory.memory}</p>
                    {memory.metadata && (
                      <div className="text-xs text-gray-500">
                        Source: {memory.metadata.source || 'unknown'}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Brain className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No memories stored yet</p>
                <p className="text-sm text-gray-400">Start a conversation to build memory</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
